// KPI 모니터링 관련 유틸리티 함수

import { TARGET_TYPES, TRANSMIT_CYCLES } from './types';

// 성취율 계산 함수
export const calculateAchievementRate = (measuredValue, targetValue, targetType) => {
  if (!measuredValue || !targetValue || targetValue === 0) return 0;
  
  if (targetType === TARGET_TYPES.INCREASE) {
    // 높을수록 좋은 지표 (생산량, 품질 점수 등)
    return Math.min(100, (measuredValue / targetValue) * 100);
  } else if (targetType === TARGET_TYPES.DECREASE) {
    // 낮을수록 좋은 지표 (불량률, 비용 등)
    return Math.min(100, (targetValue / measuredValue) * 100);
  }
  
  return 0;
};

// 성취률에 따른 색상 반환
export const getAchievementColor = (achievementRate) => {
  if (achievementRate >= 100) return '#4caf50'; // 초록
  if (achievementRate >= 90) return '#8bc34a';  // 연두
  if (achievementRate >= 80) return '#ffeb3b';  // 노랑
  if (achievementRate >= 70) return '#ff9800';  // 주황
  if (achievementRate >= 60) return '#ff5722';  // 주황빨강
  return '#f44336'; // 빨강
};

// 성취률에 따른 상태 텍스트 반환
export const getAchievementStatus = (achievementRate) => {
  if (achievementRate >= 100) return '목표 달성';
  if (achievementRate >= 90) return '우수';
  if (achievementRate >= 80) return '양호';
  if (achievementRate >= 70) return '보통';
  if (achievementRate >= 60) return '개선 필요';
  return '미흡';
};

// 전송주기에 따른 데이터 집계 기간 계산
export const getAggregationPeriod = (transmitCycle, baseDate = new Date()) => {
  const date = new Date(baseDate);
  
  switch (transmitCycle) {
    case TRANSMIT_CYCLES.DAILY:
      return {
        start: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
        end: new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
      };
    
    case TRANSMIT_CYCLES.WEEKLY:
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
      return { start: startOfWeek, end: endOfWeek };
    
    case TRANSMIT_CYCLES.MONTHLY:
      return {
        start: new Date(date.getFullYear(), date.getMonth(), 1),
        end: new Date(date.getFullYear(), date.getMonth() + 1, 1)
      };
    
    default:
      return { start: date, end: date };
  }
};

// 날짜 범위에 따른 데이터 필터링
export const filterDataByDateRange = (data, startDate, endDate) => {
  return data.filter(item => {
    const itemDate = new Date(item.aggregationDate || item.dataDate);
    return itemDate >= startDate && itemDate < endDate;
  });
};

// 회사별 KPI 데이터 그룹화
export const groupKPIDataByCompany = (kpiData) => {
  return kpiData.reduce((acc, kpi) => {
    const companyCode = kpi.companyCode || kpi.COMP_CD;
    if (!acc[companyCode]) {
      acc[companyCode] = [];
    }
    acc[companyCode].push(kpi);
    return acc;
  }, {});
};

// KPI 지표별 데이터 그룹화
export const groupKPIDataByIndicator = (kpiData) => {
  return kpiData.reduce((acc, kpi) => {
    const indicatorCode = kpi.kpiIndicatorCode || kpi.KPI_INDICATOR_CD;
    if (!acc[indicatorCode]) {
      acc[indicatorCode] = [];
    }
    acc[indicatorCode].push(kpi);
    return acc;
  }, {});
};

// 카테고리별 데이터 그룹화
export const groupKPIDataByCategory = (kpiData) => {
  return kpiData.reduce((acc, kpi) => {
    const categoryCode = kpi.categoryCode || kpi.CATEGORY_CD;
    if (!acc[categoryCode]) {
      acc[categoryCode] = [];
    }
    acc[categoryCode].push(kpi);
    return acc;
  }, {});
};

// 지부 전체 통계 계산
export const calculateBranchStatistics = (kpiData) => {
  const groupedByIndicator = groupKPIDataByIndicator(kpiData);
  const statistics = {};
  
  Object.entries(groupedByIndicator).forEach(([indicatorCode, data]) => {
    const achievements = data
      .filter(kpi => kpi.achievementRate !== null && kpi.achievementRate !== undefined)
      .map(kpi => kpi.achievementRate || 0);
    
    if (achievements.length > 0) {
      statistics[indicatorCode] = {
        average: achievements.reduce((sum, rate) => sum + rate, 0) / achievements.length,
        min: Math.min(...achievements),
        max: Math.max(...achievements),
        count: achievements.length,
        indicatorName: data[0].kpiIndicatorName || data[0].KPI_INDICATOR_NM,
        categoryCode: data[0].categoryCode || data[0].CATEGORY_CD,
        unit: data[0].unit || data[0].UNIT
      };
    }
  });
  
  return statistics;
};

// 트렌드 데이터 계산 (이전 기간 대비 변화율)
export const calculateTrend = (currentValue, previousValue) => {
  if (!previousValue || previousValue === 0) return 0;
  return ((currentValue - previousValue) / previousValue) * 100;
};

// 트렌드 방향 반환 (up, down, stable)
export const getTrendDirection = (trendPercentage, threshold = 1) => {
  if (Math.abs(trendPercentage) < threshold) return 'stable';
  return trendPercentage > 0 ? 'up' : 'down';
};

// 데이터 품질 점수에 따른 신뢰도 표시
export const getDataReliability = (qualityScore) => {
  if (qualityScore >= 95) return { level: 'high', text: '높음', color: '#4caf50' };
  if (qualityScore >= 80) return { level: 'medium', text: '보통', color: '#ff9800' };
  return { level: 'low', text: '낮음', color: '#f44336' };
};

// 숫자 포맷팅 (천 단위 콤마)
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// 퍼센트 포맷팅
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toFixed(decimals)}%`;
};

// 날짜 포맷팅
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  if (!date) return '-';
  const d = new Date(date);
  
  switch (format) {
    case 'YYYY-MM-DD':
      return d.toISOString().split('T')[0];
    case 'MM/DD':
      return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}`;
    case 'HH:mm':
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    default:
      return d.toLocaleDateString('ko-KR');
  }
};

// 전송주기 뱃지 색상 반환
export const getTransmitCycleBadgeColor = (transmitCycle) => {
  switch (transmitCycle) {
    case TRANSMIT_CYCLES.DAILY:
      return '#2196f3'; // 파란색
    case TRANSMIT_CYCLES.WEEKLY:
      return '#ff9800'; // 주황색
    case TRANSMIT_CYCLES.MONTHLY:
      return '#4caf50'; // 초록색
    default:
      return '#9e9e9e'; // 회색
  }
};

// 전송주기 라벨 기반 배지 색상 반환
export const getTransmitCycleBadgeColorByLabel = (transmitCycleLabel) => {
  switch (transmitCycleLabel) {
    case '일별':
      return '#2196f3'; // 파란색
    case '주별':
      return '#ff9800'; // 주황색
    case '월별':
      return '#4caf50'; // 초록색
    default:
      return '#9e9e9e'; // 회색
  }
};

// 전송주기별 기준일 계산
export const getBaseDataDate = (transmitCycle, currentDate = new Date()) => {
  const today = new Date(currentDate);
  
  switch (transmitCycle) {
    case TRANSMIT_CYCLES.DAILY:
      // 일별: 전일 (어제)
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return yesterday;
      
    case TRANSMIT_CYCLES.WEEKLY:
      // 주별: 이전 주의 마지막 날 (전주 일요일)
      const lastWeekEnd = new Date(today);
      const dayOfWeek = today.getDay();
      lastWeekEnd.setDate(today.getDate() - dayOfWeek - 1);
      return lastWeekEnd;
      
    case TRANSMIT_CYCLES.MONTHLY:
      // 월별: 이전 달의 마지막 날
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return lastMonth;
      
    default:
      return today;
  }
};

// 전송주기별 기준일 텍스트 반환
export const getBaseDataDateText = (transmitCycle, currentDate = new Date()) => {
  const baseDate = getBaseDataDate(transmitCycle, currentDate);
  
  switch (transmitCycle) {
    case TRANSMIT_CYCLES.DAILY:
      return `${baseDate.getFullYear()}.${(baseDate.getMonth() + 1).toString().padStart(2, '0')}.${baseDate.getDate().toString().padStart(2, '0')}`;
      
    case TRANSMIT_CYCLES.WEEKLY:
      // 해당 주의 시작일(월요일)부터 끝일(일요일)까지 표시
      const weekStart = new Date(baseDate);
      weekStart.setDate(baseDate.getDate() - 6);
      return `${weekStart.getFullYear()}.${(weekStart.getMonth() + 1).toString().padStart(2, '0')}.${weekStart.getDate().toString().padStart(2, '0')} ~ ${baseDate.getFullYear()}.${(baseDate.getMonth() + 1).toString().padStart(2, '0')}.${baseDate.getDate().toString().padStart(2, '0')}`;
      
    case TRANSMIT_CYCLES.MONTHLY:
      return `${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월`;
      
    default:
      return formatDate(baseDate);
  }
};